import { useEffect, useState, useCallback } from 'react';
import { gql, useApolloClient } from '@apollo/client';
import { invoiceDetailInvoiceFragment } from '../blocks/InvoiceDetail';
import { useToast } from './useToast';
import {
	useNewEventsSubscription_GetAuthedUserQuery,
	useNewEventsSubscription_UpdateSelfMutation,
	useNewEventsSubscription_GetEventsWhereLazyQuery,
	useNewEventsSubscription_EventCreatedSubscription,
	UserType,
} from '#types';
import { FlattenedUser } from '../types/user';
import { useSelector } from 'react-redux';
import rpcShared from '@rockpapercoin/rpc-shared';
import dayjs from 'dayjs';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const newEventsSubscription_GetAuthedUser = gql`
	query newEventsSubscription_GetAuthedUser($where: UserWhereUniqueInput!) {
		getAuthedUser(where: $where) {
			id
			userType
			eventsLastViewedAt
		}
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const newEventsSubscription_UpdateSelf = gql`
	mutation newEventsSubscription_UpdateSelf($data: UserUpdateSelfInput!) {
		updateSelf(data: $data) {
			id
			eventsLastViewedAt
		}
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const newEventsSubscription_GetEventsWhere = gql`
	query newEventsSubscription_GetEventsWhere($where: EventWhereInput!) {
		getEventsWhere(where: $where) {
			id
			category
			createdAt
			consumingUsers {
				id
			}
			dismissingUsers {
				id
			}
		}
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const newEventsSubscription_EventCreated = gql`
	subscription newEventsSubscription_EventCreated {
		eventCreated {
			id
			invoice {
				...invoiceDetailInvoice
			}
		}
	}
	${ invoiceDetailInvoiceFragment }
`;

export const useNewEventsSubscription = () => {
	const client = useApolloClient();
	const user: FlattenedUser = useSelector( ( state: any ) => state.user );
	const { showError } = useToast();
	const [ badgeContent, setBadgeContent ] = useState<number>();
	const [ eventsLastViewedAt, setEventsLastViewedAt ] = useState<
	string | Date | undefined
	>();

	const [ updateSelf ] = useNewEventsSubscription_UpdateSelfMutation();

	const reduxUser = user?.isLoggedIn ? user : undefined;

	const authedUserQuery = useNewEventsSubscription_GetAuthedUserQuery( {
		variables: { where: { id: reduxUser?.user.id } },
		skip: !reduxUser,
	} );

	useEffect( () => {
		if ( authedUserQuery.error ) {
			showError( authedUserQuery.error );
		} else if ( authedUserQuery.data?.getAuthedUser ) {
			setEventsLastViewedAt(
				authedUserQuery.data.getAuthedUser.eventsLastViewedAt
			);
		}
	}, [ authedUserQuery, showError ] );

	const authenticatedUser = authedUserQuery.data?.getAuthedUser;
	const skip = !authenticatedUser || !eventsLastViewedAt;
	const [ getEventsWhere, { error: queryError } ] =
		useNewEventsSubscription_GetEventsWhereLazyQuery();

	const eventsCallback = useCallback( async () => {
		if ( skip ) {
			return;
		}
		const res = await getEventsWhere( {
			variables: {
				where: {
					createdAt: { gt: eventsLastViewedAt },
					consumingUsers: { some: { id: authenticatedUser?.id } },
					dismissingUsers: { every: { NOT: [ { id: authenticatedUser?.id } ] } },
					NOT: [
						{
							category: {
								in: rpcShared.events.feed.getEventFeedEventsNotHandled(
									( authenticatedUser?.userType || 'GuestUser' ) as UserType
								),
							},
						},
					],
				},
			},
		} );
		if ( res?.data?.getEventsWhere ) {
			return res;
		}
	}, [
		getEventsWhere,
		eventsLastViewedAt,
		authenticatedUser,
		skip
	] );

	useEffect( () => {
		const getEvents = async () => {
			const res = await eventsCallback();
			if ( res?.data?.getEventsWhere ) {
				setBadgeContent( res.data.getEventsWhere.length );
			}
		};
		getEvents();
	}, [
		skip,
		showError,
		eventsCallback,
		eventsLastViewedAt,
		authenticatedUser
	] );

	const { data: subscription, error: subscriptionError } =
		useNewEventsSubscription_EventCreatedSubscription( {
			skip,
		} );

	useEffect( () => {
		const updateAfterEventCreation = async () => {
			const res = await eventsCallback();

			const subscriptionValue = res?.data?.getEventsWhere?.filter(
				( event ) =>
					authenticatedUser &&
					dayjs( event.createdAt ).isAfter( dayjs( eventsLastViewedAt ) )
			).length;
			if ( typeof subscriptionValue === 'number' ) {
				setBadgeContent( subscriptionValue );

				const newEventIsInFeed = res?.data?.getEventsWhere?.some(
					( event ) => event.id === subscription?.eventCreated.id
				);
				if ( newEventIsInFeed ) {
					await client.refetchQueries( {
						updateCache: ( cache ) => {
							cache.evict( {
								id: 'ROOT_QUERY',
								fieldName: 'getActionableItemsForClient',
							} );
						},
					} );
				}
			}
		};
		updateAfterEventCreation();
	}, [
		authenticatedUser,
		client,
		eventsLastViewedAt,
		eventsCallback,
		subscription,
		skip,
	] );

	useEffect( () => {
		if ( queryError ) {
			showError( queryError );
		}
	}, [ queryError, showError ] );

	useEffect( () => {
		if ( subscriptionError ) {
			showError( subscriptionError );
		}
	}, [ subscriptionError, showError ] );

	const reset = useCallback( async () => {
		const now = new Date();
		const response = await updateSelf( {
			variables: {
				data: { eventsLastViewedAt: now },
			},
		} );
		if ( response?.errors ) {
			response.errors.forEach( ( error ) => showError( error ) );
		} else if ( response?.data ) {
			setEventsLastViewedAt( now );
			setBadgeContent( 0 );
		}
	}, [ updateSelf, showError ] );

	return {
		badgeContent,
		reset,
	};
};
